import { Directive, ElementRef, OnInit, OnChanges, SimpleChanges, OnDestroy, inject, input } from '@angular/core';
import { I18nService } from '../i18n.service';
import { Subscription } from 'rxjs';

/**
 * The TranslateDirective sets the text content of your element based on the key you provide.
 *
 * Translations will usefallbacks to the default language, or to just displaying the translation key if no translation is available in any current or fallback language.
 *
 * @param trans The translation key to use.
 * @param transLocale Force the application to prefer this exact locale over any others, and then use normal fallbacks if necessary.
 *
 * @example // Basic translation
 * <p trans="some.example.key">
 *   (This paragraph's text content will be replaced by the value at some.example.key in the translation files.)
 * </p>
 *
 * @example // Translation with forced locale
 * <p trans="some.example.key" transLocale="pl-PL">
 *   (This paragraph will prefer a value in pl-PL even if that's not the current language.)
 * </p>
 */
@Directive({
  selector: '[trans]',
  standalone: true,
})
export class TranslateDirective implements OnInit, OnChanges, OnDestroy {
  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly i18n = inject(I18nService);

  private subscription: Subscription | undefined;

  public trans = input('');
  public transLocale = input<string | undefined>(undefined);

  ngOnInit() {
    this.draw();
    this.subscription = this.i18n.updated.subscribe(() => {
      this.draw();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.trans || changes.transLocale) {
      this.draw();
    }
  }

  draw() {
    const text = this.i18n.getPhrase(this.trans(), { lang: this.transLocale() });
    this.el.nativeElement.textContent = text;
  }
}
